/* global AbortController, crypto, document, setTimeout, clearTimeout */

import { heartbeatConnectorPresence, removeConnectorPresence } from "./connectorPairingClient.js";
import { createProjectBindingHashes } from "./projectBinding.js";

function createPresenceId() {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return `tp-${Array.from(bytes)
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
}

export class ConnectorDiscoveryController {
  constructor({ onCandidates, onCompleted, onError } = {}) {
    this._onCandidates = onCandidates || (() => {});
    this._onCompleted = onCompleted || (() => {});
    this._onError = onError || (() => {});
    this._generation = 0;
    this._timer = null;
    this._abortController = null;
    this._presenceId = "";
    this._environmentId = "";
    this._bindingHashes = [];
    this._config = null;
    this._frontendVersion = "";
    this._lastCandidates = [];
    this._completedPairingIds = new Set();
    this._visibilityHandler = () => this.refreshNow();
  }

  get presenceId() {
    return this._presenceId;
  }

  async updateProject({ stableIdentity, environmentId, config, frontendVersion = "" }) {
    const generation = ++this._generation;
    const oldPresenceId = this._presenceId;
    this._clearTransport();
    this._presenceId = "";
    this._bindingHashes = [];
    this._lastCandidates = [];
    this._completedPairingIds.clear();
    this._onCandidates([]);
    if (oldPresenceId) removeConnectorPresence(oldPresenceId).catch(() => {});

    if (!config?.enabled || !environmentId) return;
    const hashes = await createProjectBindingHashes(
      stableIdentity,
      environmentId,
      config.max_bindings || 3
    );
    if (generation !== this._generation || hashes.length === 0) return;
    this._presenceId = createPresenceId();
    this._environmentId = environmentId;
    this._bindingHashes = hashes;
    this._config = config;
    this._frontendVersion = frontendVersion;
    document.addEventListener("visibilitychange", this._visibilityHandler);
    this._heartbeat(generation);
  }

  refreshNow() {
    if (!this._presenceId) return;
    if (this._timer) clearTimeout(this._timer);
    this._timer = null;
    this._heartbeat(this._generation);
  }

  markApproved(candidate) {
    if (!candidate?.pairing_id) return;
    const approved = { ...candidate, status: "approved" };
    const index = this._lastCandidates.findIndex(
      (value) => value.pairing_id === approved.pairing_id
    );
    if (index >= 0) this._lastCandidates[index] = approved;
    else this._lastCandidates.push(approved);
  }

  stop({ remove = false, keepalive = false } = {}) {
    this._generation += 1;
    const presenceId = this._presenceId;
    this._clearTransport();
    this._presenceId = "";
    this._bindingHashes = [];
    this._lastCandidates = [];
    this._completedPairingIds.clear();
    this._onCandidates([]);
    if (remove && presenceId) {
      removeConnectorPresence(presenceId, { keepalive }).catch(() => {});
    }
  }

  _clearTransport() {
    if (this._timer) clearTimeout(this._timer);
    this._timer = null;
    this._abortController?.abort();
    this._abortController = null;
    document.removeEventListener("visibilitychange", this._visibilityHandler);
  }

  _schedule(generation, seconds) {
    if (generation !== this._generation || !this._presenceId) return;
    if (this._timer) clearTimeout(this._timer);
    this._timer = setTimeout(
      () => {
        this._timer = null;
        this._heartbeat(generation);
      },
      Math.max(1, Number(seconds) || 5) * 1000
    );
  }

  async _heartbeat(generation) {
    if (generation !== this._generation || !this._presenceId || this._abortController) {
      return;
    }
    const abortController = new AbortController();
    this._abortController = abortController;
    try {
      const response = await heartbeatConnectorPresence(
        {
          presence_id: this._presenceId,
          environment_id: this._environmentId,
          project_binding_hashes: this._bindingHashes,
          frontend_version: this._frontendVersion,
          visibility: document.visibilityState === "hidden" ? "hidden" : "visible",
        },
        abortController.signal
      );
      if (generation !== this._generation) return;
      const candidates = Array.isArray(response.candidates) ? response.candidates : [];
      const consumedCandidate = candidates.find(
        (candidate) =>
          candidate.status === "consumed" && !this._completedPairingIds.has(candidate.pairing_id)
      );
      const activeCandidates = candidates.filter((candidate) => candidate.status !== "consumed");
      const disappearedApprovedCandidate = this._lastCandidates.find(
        (candidate) =>
          candidate.status === "approved" &&
          !candidates.some((next) => next.pairing_id === candidate.pairing_id)
      );
      const completedCandidate = consumedCandidate || disappearedApprovedCandidate;
      if (completedCandidate?.pairing_id) {
        this._completedPairingIds.add(completedCandidate.pairing_id);
      }
      this._lastCandidates = activeCandidates;
      this._onCandidates(activeCandidates, this._presenceId);
      if (completedCandidate) this._onCompleted(completedCandidate);
      this._schedule(generation, response.next_poll_seconds);
    } catch (error) {
      if (error?.name !== "AbortError" && generation === this._generation) {
        this._onError(error);
        const fallback =
          document.visibilityState === "hidden"
            ? this._config?.background_poll_seconds
            : this._config?.active_poll_seconds;
        this._schedule(generation, Math.max(10, Number(fallback) || 10));
      }
    } finally {
      if (this._abortController === abortController) this._abortController = null;
    }
  }
}
